// Payslip → PDF engine, shared by the salary page's "Print payslip" button and
// the "salary paid" notification (which downloads the PDF before opening the
// page). Takes the rendered `.printable-payslip` element (PrintablePayslip),
// clones it off-screen, rasterizes it with html2canvas and paginates it into
// an A4 jsPDF, cutting only between rows. Throws on failure — callers decide
// how to report it.

const loadScript = (src) => new Promise((resolve, reject) => {
  if (document.querySelector(`script[src="${src}"]`)) return resolve();
  const s = document.createElement('script');
  s.src = src; s.onload = resolve; s.onerror = reject;
  document.head.appendChild(s);
});

export async function exportPayslipPdf(el, { isRTL, fileName }) {
  if (!el) throw new Error('Payslip not found');
  if (!window.html2canvas) await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
  if (!window.jspdf)       await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');

  const clone = el.cloneNode(true);
  clone.style.cssText = 'display:block;position:absolute;left:-9999px;top:0;width:800px;';
  const style = document.createElement('style');
  // html2canvas renders text character-by-character whenever letter-spacing is
  // set, which breaks Arabic letter joining and word order. Force it off in RTL.
  style.innerHTML = '.printable-payslip,.printable-payslip *{color:#111!important;-webkit-text-fill-color:#111!important;}'
    + (isRTL ? '.printable-payslip,.printable-payslip *{letter-spacing:normal!important;word-spacing:normal!important;text-transform:none!important;}' : '');
  clone.insertBefore(style, clone.firstChild);
  document.body.appendChild(clone);

  try {
    // Wait two animation frames so the browser fully computes layout
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

    const scale = Math.max(2, window.devicePixelRatio || 2);
    const pageWidth = 595.28;
    const pageHeight = 841.89;
    const margin = 20;
    const imgWidth = pageWidth - margin * 2;

    // ── Collect safe Y break points BEFORE rendering ──────────────────────
    // html2canvas ignores CSS print rules entirely, so we must find safe
    // cut positions ourselves by reading the rendered DOM element boundaries.
    // We cut only at the BOTTOM edge of a row/heading — never through one.
    const cloneRect = clone.getBoundingClientRect();
    const safeBreakSet = new Set([0]);

    // Hard section starts: always begin these sections at the top of a new PDF page.
    const forcedSectionStarts = [];
    clone.querySelectorAll('.allowance-instances-print-section, .deduction-instances-print-section').forEach((node) => {
      const r = node.getBoundingClientRect();
      const topPx = Math.round((r.top - cloneRect.top) * scale);
      if (topPx > 0) forcedSectionStarts.push(topPx);
    });
    forcedSectionStarts.sort((a, b) => a - b);

    clone.querySelectorAll('tr, .table-page, .pdf-section-start').forEach(node => {
      const r = node.getBoundingClientRect();
      // top of element = safe place to start a new page
      const topPx    = Math.round((r.top    - cloneRect.top) * scale);
      // bottom of element = safe place to end a page
      const bottomPx = Math.round((r.bottom - cloneRect.top) * scale);
      if (topPx    > 0) safeBreakSet.add(topPx);
      if (bottomPx > 0) safeBreakSet.add(bottomPx);
    });

    const safeBreaks = Array.from(safeBreakSet).sort((a, b) => a - b);

    const canvas = await window.html2canvas(clone, { scale, useCORS: true, backgroundColor: '#ffffff' });
    const jsPDFCtor = window.jspdf?.jsPDF || window.jsPDF || window.jspdf;
    if (!jsPDFCtor) throw new Error('jsPDF not available');

    const pdf = new jsPDFCtor('p', 'pt', 'a4');
    const pxPerPt   = canvas.width / imgWidth;
    const maxPagePx = Math.floor((pageHeight - margin * 2) * pxPerPt);

    let srcY = 0, page = 0;
    while (srcY < canvas.height) {
      const idealCut = srcY + maxPagePx;

      let cutY;
      if (idealCut >= canvas.height) {
        // Last (or only) page — take everything remaining
        cutY = canvas.height;
      } else {
        // If a forced section start exists inside this page window, cut exactly there
        // so that section begins at the top of the next page.
        let forcedCut = -1;
        for (let i = 0; i < forcedSectionStarts.length; i++) {
          const point = forcedSectionStarts[i];
          if (point > srcY && point <= idealCut) {
            forcedCut = point;
            break;
          }
        }

        if (forcedCut > 0) {
          cutY = forcedCut;
        } else {
          // Find the last safe break point that fits within this page
          let lastCandidate = -1;
          for (let i = 0; i < safeBreaks.length; i++) {
            const point = safeBreaks[i];
            if (point > srcY && point <= idealCut) lastCandidate = point;
            if (point > idealCut) break;
          }
          // No safe break found (e.g. a single row taller than a page) — cut at page boundary
          cutY = lastCandidate > 0 ? lastCandidate : idealCut;
        }
      }

      const h = cutY - srcY;
      if (h <= 0) { srcY = cutY + 1; continue; } // safety guard

      const pc = document.createElement('canvas');
      pc.width = canvas.width; pc.height = h;
      const ctx = pc.getContext('2d');
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, pc.width, h);
      ctx.drawImage(canvas, 0, srcY, canvas.width, h, 0, 0, canvas.width, h);

      if (page > 0) pdf.addPage();
      pdf.addImage(pc.toDataURL('image/png'), 'PNG', margin, margin, imgWidth, h / pxPerPt);
      srcY = cutY;
      page++;
    }

    pdf.save(fileName);
  } finally {
    clone.remove();
  }
}

export const payslipFileName = (name, monthLabel, year) =>
  `Payslip-${String(name || 'employee').replace(/\s+/g, '_')}-${monthLabel}-${year}.pdf`;
